import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { Asset } from '../types';
import { formatVND, formatDate, mapStatusToVN, mapTechnicalConditionToVN } from '../utils/formatters';

// Đăng ký font Open Sans (nếu đã có file font .ttf trong public/fonts hoặc src/fonts)
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Regular.ttf', fontWeight: 'normal', fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Bold.ttf', fontWeight: 'bold', fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Italic.ttf', fontWeight: 'normal', fontStyle: 'italic' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-BoldItalic.ttf', fontWeight: 'bold', fontStyle: 'italic' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-SemiBold.ttf', fontWeight: 600, fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-SemiBoldItalic.ttf', fontWeight: 600, fontStyle: 'italic' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Medium.ttf', fontWeight: 500, fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-MediumItalic.ttf', fontWeight: 500, fontStyle: 'italic' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Light.ttf', fontWeight: 300, fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-LightItalic.ttf', fontWeight: 300, fontStyle: 'italic' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-ExtraBold.ttf', fontWeight: 800, fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-ExtraBoldItalic.ttf', fontWeight: 800, fontStyle: 'italic' });

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Open Sans',
    fontSize: 11,
    padding: 32,
    backgroundColor: '#fff',
  },
  headerBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f6faff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  qrBox: {
    width: 90,
    height: 90,
    marginRight: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderRadius: 6,
    border: '1pt solid #e0e0e0',
  },
  barcodeBox: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  assetName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#222',
    marginBottom: 2,
  },
  assetCode: {
    fontSize: 12,
    color: '#1677ff',
    fontWeight: 'bold',
    marginBottom: 4,
  },
  section: {
    marginBottom: 12,
    padding: 0,
    backgroundColor: '#fff',
  },
  blockTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#666',
    backgroundColor: '#f0f0f0',
    textTransform: 'uppercase',
    borderRadius: 6,
    padding: 6,
  },
  twoColWrap: {
    flexDirection: 'row',
    gap: 24,
  },
  col: {
    flex: 1,
    minWidth: 180,
    maxWidth: '50%',
    marginRight: 24,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 10,
    alignItems: 'flex-start',
  },
  label: {
    color: '#333',
    fontWeight: 'normal',
    textTransform: 'uppercase',
  },
  value: {
    marginLeft: 6,
    color: '#222',
    fontWeight: 'semibold',
    textAlign: 'left',
    
  },
  descRow: {
    flexDirection: 'row',
    marginBottom: 3,
    alignItems: 'flex-start',
  },
  descLabel: {
    color: '#333',
    fontWeight: 'normal',
    marginTop: 2,
    textTransform: 'uppercase',
  },
  descValue: {
    marginLeft: 6,
    color: '#222',
    fontWeight: 'semibold',
    textAlign: 'left',
    flex: 1,
    flexWrap: 'wrap',
    wordWrap: 'break-word',
  },
});







interface AssetPDFDocumentProps {
  asset: Asset;
  companies: any[];
  categories: any[];
  locations: any[];
  suppliers: any[];
  employees: any[];
  maintenanceTypes?: any[];
  qrBase64?: string;
  barcodeBase64?: string;
}



// Header và Footer cho PDF (chuẩn @react-pdf/renderer)
const PdfHeader = (
  <View fixed style={{ width: '100%', textAlign: 'left', marginBottom: 12 }}>
    <Text style={{ fontSize: 15, fontWeight: 'bold', letterSpacing: 0.5 }}>PHIẾU KIỂM KÊ TÀI SẢN</Text>
    <View style={{ borderBottom: '1pt solid #888', marginTop: 4, marginBottom: 8, width: '100%' }} />
  </View>
);

const PdfFooter = (
  <View fixed style={{ width: '100%', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', position: 'absolute', bottom: 16, right: 32 }}>
    <Text style={{ fontSize: 9, color: '#888' }} render={({ pageNumber, totalPages }) => `Trang ${pageNumber} / ${totalPages}`}></Text>
  </View>
);

const AssetPDFDocument: React.FC<AssetPDFDocumentProps> = ({ asset, companies, categories, locations, suppliers, employees, maintenanceTypes = [], qrBase64, barcodeBase64 }) => {
  let specs = asset.technical_specifications;
  if (typeof specs === 'string') {
    try { specs = JSON.parse(specs); } catch { specs = {}; }
  }
  const companyName = companies.find((c: any) => c.id === asset.company_id)?.name || '—';
  const categoryName = categories.find((c: any) => c.id === asset.category_id)?.name || '—';
  const locationName = locations.find((l: any) => l.id === asset.location_id)?.name || '—';
  const supplierName = suppliers.find((s: any) => s.id === asset.supplier_id)?.name || '—';
  const employeeName = employees.find((e: any) => e.id === asset.assigned_to_employee_id)?.full_name || '—';
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {PdfHeader}
        {/* Nội dung trang 1 */}
        <View style={styles.section}>
            <Text style={styles.blockTitle}>Thông tin chung</Text>
            <View style={styles.headerBox}>
              <View style={styles.qrBox}>
                {qrBase64 ? (
                  <Image src={qrBase64} style={{ width: 80, height: 80 }} />
                ) : (
                  <Text>QR</Text>
                )}
              </View>
              <View style={styles.barcodeBox}>
                <Text style={styles.assetName}>{asset.name}</Text>
                <Text style={styles.assetCode}>{asset.asset_code}</Text>
                {barcodeBase64 ? (
                  <Image src={barcodeBase64} style={{ width: 120, height: 32 }} />
                ) : (
                  <Text>BARCODE</Text>
                )}
              </View>
            </View>
            <View style={styles.twoColWrap}>
              <View style={styles.col}>
                <View style={styles.row}><Text style={styles.label}>Công ty:</Text><Text style={styles.value}>{companyName}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Tên tài sản:</Text><Text style={styles.value}>{asset.name}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Vị trí:</Text><Text style={styles.value}>{locationName}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Tình trạng:</Text><Text style={styles.value}>{mapStatusToVN(asset.status)}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Tài sản cha:</Text><Text style={styles.value}>{asset.parent_asset_id || '—'}</Text></View>
              </View>
              <View style={styles.col}>
                <View style={styles.row}><Text style={styles.label}>Mã tài sản:</Text><Text style={styles.value}>{asset.asset_code}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Danh mục:</Text><Text style={styles.value}>{categoryName}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Nhân viên:</Text><Text style={styles.value}>{employeeName}</Text></View>
                <View style={styles.row}><Text style={styles.label}>Tình trạng kỹ thuật:</Text><Text style={styles.value}>{mapTechnicalConditionToVN(asset.technical_condition || '')}</Text></View>
              </View>
            </View>
            {/* Mô tả full width */}
            <View style={styles.descRow}>
              <Text style={styles.descLabel}>Mô tả:</Text>
              <Text style={styles.descValue}>{asset.description || '—'}</Text>
            </View>
          </View>
          {/* Thông số kỹ thuật */}
          <View style={styles.section}>
            <Text style={styles.blockTitle}>Thông số kỹ thuật</Text>
            {(() => {
              const entries = specs && typeof specs === 'object' ? Object.entries(specs) : [];
              // Định nghĩa thứ tự mong muốn
              const order = ['Thương hiệu', 'Model', 'Tiêu thụ', 'Năng suất'];
              const getEntry = (key: string) => entries.find(([k]) => k.toLowerCase() === key.toLowerCase());
              const col1 = [getEntry('Thương hiệu'), getEntry('Model')].filter((e): e is [string, unknown] => !!e);
              const col2 = [getEntry('Tiêu thụ'), getEntry('Năng suất')].filter((e): e is [string, unknown] => !!e);
              // Các trường còn lại (không phải 4 trường trên và không phải mô tả)
              const usedKeys = new Set([...col1, ...col2].map(([k]) => k));
              const others = entries.filter(([k]) => !order.some(o => o.toLowerCase() === k.toLowerCase()) && !k.toLowerCase().includes('mô tả'));
              // Mô tả (nếu có)
              const descs = entries.filter(([k]) => k.toLowerCase().includes('mô tả'));
              return (
                <>
                  <View style={styles.twoColWrap}>
                    <View style={styles.col}>
                      {col1.map((entry) => {
                        if (!entry) return null;
                        const [key, value] = entry;
                        return (
                          <View style={styles.row} key={key}>
                            <Text style={styles.label}>{key}:</Text>
                            <Text style={styles.value}>{value === undefined || value === null || value === '' ? '—' : String(value)}</Text>
                          </View>
                        );
                      })}
                    </View>
                    <View style={styles.col}>
                      {col2.map((entry) => {
                        if (!entry) return null;
                        const [key, value] = entry;
                        return (
                          <View style={styles.row} key={key}>
                            <Text style={styles.label}>{key}:</Text>
                            <Text style={styles.value}>{value === undefined || value === null || value === '' ? '—' : String(value)}</Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                  {/* Các trường khác (nếu có) */}
                  {others.map(([key, value]) => (
                    <View style={styles.row} key={key}>
                      <Text style={styles.label}>{key}:</Text>
                      <Text style={styles.value}>{value === undefined || value === null || value === '' ? '—' : String(value)}</Text>
                    </View>
                  ))}
                  {/* Dòng mô tả full width */}
                  {descs.map(([key, value]) => (
                    <View style={styles.descRow} key={key}>
                      <Text style={styles.descLabel}>{key}:</Text>
                      <Text style={styles.descValue}>{value === undefined || value === null || value === '' ? '—' : String(value)}</Text>
                    </View>
                  ))}
                </>
              );
            })()}
          </View>
          {/* Thông tin mua sắm */}
          <View style={styles.section}>
            <Text style={styles.blockTitle}>Thông tin mua sắm</Text>
            <View style={styles.twoColWrap}>
              <View style={styles.col}>
                <View style={styles.row}><Text style={styles.label}>Giá trị:</Text><Text style={styles.value}>{formatVND(asset.purchase_cost)}</Text></View>
              </View>
              <View style={styles.col}>
                <View style={styles.row}><Text style={styles.label}>Ngày mua:</Text><Text style={styles.value}>{formatDate(asset.purchase_date)}</Text></View>
              </View>
            </View>
            {/* Nhà cung cấp full width */}
            <View style={styles.row}>
              <Text style={styles.label}>Nhà cung cấp:</Text>
              <Text style={styles.value}>{supplierName}</Text>
            </View>
          </View>
          {/* Bảo hành & kiểm kê */}
          <View style={styles.section}>
            <Text style={styles.blockTitle}>Bảo hành & kiểm kê</Text>
            <View style={styles.twoColWrap}>
              <View style={styles.col}>
                <View style={styles.row}><Text style={styles.label}>Ngày hết hạn bảo hành:</Text><Text style={styles.value}>{formatDate(asset.warranty_expiry_date)}</Text></View>
              </View>
              <View style={styles.col}>
                <View style={styles.row}><Text style={styles.label}>Ngày kiểm kê gần nhất:</Text><Text style={styles.value}>{formatDate(asset.last_audit_date)}</Text></View>
              </View>
            </View>
          </View>
          {/* Bảo dưỡng */}
          <View style={styles.section}>
            <Text style={styles.blockTitle}>Loại bảo dưỡng & Chu kỳ</Text>
            {Array.isArray(asset.maintenance_schedules) && asset.maintenance_schedules.length > 0 ? (
              asset.maintenance_schedules.map((ms: any, idx: number) => {
                const mt = maintenanceTypes.find((t: any) => t.id === ms.maintenance_type_id);
                return (
                  <View style={styles.row} key={ms.id || ms.maintenance_type_id || idx}>
                    <Text style={styles.label}>{mt ? mt.name.toUpperCase() : ms.maintenance_type_id}:</Text>
                    <Text style={styles.value}>Chu kỳ: {ms.cycle_months ? ms.cycle_months + ' tháng' : '—'}</Text>
                  </View>
                );
              })
            ) : (
              <Text style={styles.value}>Không có</Text>
            )}
          </View>
          {/* Accessories Table - Luôn ở trang 2 nếu có */}
          {Array.isArray(asset.accessories) && asset.accessories.length > 0 && (
            <></> // placeholder, sẽ render ở Page 2
          )}
          {PdfFooter}
        </Page>
      {/* Trang 2: Accessories Table nếu có */}
      {Array.isArray(asset.accessories) && asset.accessories.length > 0 && (
        <Page size="A4" orientation="landscape" style={styles.page}>
          {PdfHeader}
          <View style={styles.section}>
            <Text style={styles.blockTitle}>Vật tư/Thiết bị kèm theo</Text>
            <View style={{ border: '1pt solid #e0e0e0', borderRadius: 6 }}>
              <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', padding: 6 }}>
                <Text style={{ flex: 2, fontWeight: 'bold' }}>Tên vật dụng</Text>
                <Text style={{ flex: 1, fontWeight: 'bold', textAlign: 'center' }}>Số lượng</Text>
                <Text style={{ flex: 1, textAlign: 'center' }}>Đơn vị</Text>
                <Text style={{ flex: 1 }}>Giá</Text>
                <Text style={{ flex: 1 }}>Tổng giá</Text>
                <Text style={{ flex: 1 }}>Ngày mua</Text>
                <Text style={{ flex: 1.5 }}>Hồ sơ mua</Text>
                <Text style={{ flex: 2 }}>Ghi chú</Text>
              </View>
              {asset.accessories.map((acc: any, idx: number) => (
                <View key={idx} style={{ flexDirection: 'row', borderTop: '1pt solid #e0e0e0', padding: 6 }}>
                  <Text style={{ flex: 2 }}>{acc.name || '—'}</Text>
                  <Text style={{ flex: 1, textAlign: 'center' }}>{acc.quantity ?? '—'}</Text>
                  <Text style={{ flex: 1, textAlign: 'center' }}>{acc.unit || '—'}</Text>
                                  <Text style={{ flex: 1 }}>{acc.price ? Number(acc.price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '—'}</Text>
                <Text style={{ flex: 1 }}>{acc.total_price ? Number(acc.total_price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '—'}</Text>
                  <Text style={{ flex: 1 }}>{acc.purchase_date ? (new Date(acc.purchase_date)).toLocaleDateString('vi-VN') : '—'}</Text>
                  <Text style={{ flex: 1.5 }}>{acc.purchase_document || '—'}</Text>
                  <Text style={{ flex: 2 }}>{acc.notes || '—'}</Text>
                </View>
              ))}
            </View>
          </View>
          {PdfFooter}
        </Page>
      )}
    </Document>
  );
};

export default AssetPDFDocument; 